/* eslint-disable react-native/no-color-literals */
import React from 'react';
import {
  Dimensions,
  Image,
  Linking,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { BaseToast } from 'react-native-toast-message';
import { isWeb, isNative, isMobileWeb } from '~global/screens';
import Colors from '~global/Colors';
import LeadingIcon from '~global/LeadingIcon';
import TrailingIcon from '~global/TrailingIcon';

const { width } = Dimensions.get('window');
const toastWidth = width - 30;

const text1Style = {
  fontSize: 13,
  fontFamily: 'figtree-black',
  color: Colors.white,
  fontWeight: '400',
  ...(isWeb ? { paddingRight: 30 } : { width: toastWidth - 75 }),
};

const text1FeedbackStyle = { ...text1Style, color: Colors.black };

const iconSuccess = require('~assets/img/common/toast-checked.png');
const iconWarning = require('~assets/img/common/icon-warning-white.png');
const iconCheckbox = require('~assets/img/common/checkbox-checked.png');

const toastStyle = {
  height: 45,
  borderLeftWidth: 0,
  borderRadius: 100,
  width: isWeb ? 'auto' : toastWidth,
  zIndex: 9999,
};
const iconResolver = (uri) => (isNative ? uri : { uri });
const getToastHeight = ({ isVisible }) => {
  return !isMobileWeb || isVisible ? 54 : 0;
};
const getVisibilityStyle = (isVisible) => ({
  display: isVisible ? 'flex' : 'none',
});

const toastStyleBlack = { ...toastStyle, backgroundColor: '#000000' };
const toastStyleFile = {
  ...toastStyle,
  borderRadius: 8,
  backgroundColor: Colors.white,
  borderWidth: 1,
  borderColor: '#EBEBEB',
  overflow: 'hidden',
};
const text1FileStyle = {
  ...text1Style,
  color: '#231F20',
  fontFamily: 'lato',
};
const text2FileStyle = {
  fontSize: 12,
  lineHeight: 18,
  color: '#231F20',
};
const errorFileContentContainerStyle = {
  paddingHorizontal: 14,
  paddingVertical: 8,
};

const renderWarningIcon = () => (
  <LeadingIcon source={iconResolver(iconWarning)} style={styles.iconLarge} />
);

const renderCheckboxIcon = () => (
  <LeadingIcon
    source={iconResolver(iconCheckbox)}
    style={styles.iconFeedback}
  />
);

const EMAIL_PATTERN = /([\w.+-]+@[\w-]+(?:\.[\w-]+)+)/;

const linkifyEmails = (text) => {
  if (typeof text !== 'string' || !EMAIL_PATTERN.test(text)) {
    return text;
  }

  return text.split(EMAIL_PATTERN).map((part, index) =>
    EMAIL_PATTERN.test(part) ? (
      <Text
        key={index}
        style={styles.link}
        onPress={() => Linking.openURL(`mailto:${part}`).catch(() => {})}>
        {part}
      </Text>
    ) : (
      part
    ),
  );
};

export const toastConfig = {
  success: ({ text1, text2, ...rest }) => (
    <BaseToast
      {...rest}
      style={[
        toastStyle,
        { height: getToastHeight(rest) },
        getVisibilityStyle(rest.isVisible),
        { backgroundColor: Colors.purple4 },
      ]}
      text1Style={text1Style}
      text2Style={text1Style}
      renderLeadingIcon={() => (
        <LeadingIcon
          source={iconResolver(iconSuccess)}
          style={rest.isVisible ? styles.iconLarge : styles.iconHidden}
        />
      )}
      text1={text1}
      text2={text2}
    />
  ),
  update: ({ text1, ...rest }) => (
    <BaseToast
      {...rest}
      style={[toastStyleBlack, { height: getToastHeight(rest) }]}
      text1Style={text1Style}
      renderLeadingIcon={renderWarningIcon}
      text1={text1}
      text2={null}
    />
  ),
  error: ({ text1, text2, ...rest }) => (
    <BaseToast
      {...rest}
      style={[
        toastStyle,
        { height: getToastHeight(rest) },
        getVisibilityStyle(rest.isVisible),
        { backgroundColor: Colors.red0 },
      ]}
      text1Style={text1Style}
      renderLeadingIcon={renderWarningIcon}
      text1={linkifyEmails(text1)}
      text1NumberOfLines={2}
      text2={null}
    />
  ),
  feedbackSuccess: ({ text1, ...rest }) => (
    <BaseToast
      {...rest}
      style={[
        toastStyle,
        { height: getToastHeight(rest), backgroundColor: Colors.purple2 },
      ]}
      text1Style={text1FeedbackStyle}
      renderLeadingIcon={renderCheckboxIcon}
      text1={text1}
      text2={null}
    />
  ),
  errorFile: ({ text1, text2, ...rest }) => (
    <BaseToast
      {...rest}
      style={toastStyleFile}
      contentContainerStyle={errorFileContentContainerStyle}
      text1Style={text1FileStyle}
      text2Style={{
        ...text1FileStyle,
        ...text2FileStyle,
      }}
      renderLeadingIcon={() => (
        <View style={styles.errorFileLeading}>
          <Image
            source={iconResolver(iconWarning)}
            style={styles.errorFileLeadingIcon}
          />
        </View>
      )}
      text1={text1}
      text1NumberOfLines={2}
      text2={text2 || null}
    />
  ),
  custom: ({ text1, ...rest }) => (
    <BaseToast
      {...rest}
      style={[toastStyle, { height: getToastHeight(rest) }, rest?.props?.style]}
      text1Style={
        rest?.props?.text1Style
          ? { ...text1Style, ...rest.props.text1Style }
          : text1Style
      }
      renderLeadingIcon={
        rest?.props?.leadingIcon
          ? () => (
              <LeadingIcon
                source={iconResolver(rest.props.leadingIcon)}
                style={styles.iconCustomLeading}
              />
            )
          : undefined
      }
      renderTrailingIcon={
        rest?.props?.trailingIcon
          ? () => (
              <TrailingIcon
                source={iconResolver(rest.props.trailingIcon)}
                style={rest?.props?.trailingIconStyle}
                onPress={() => rest?.props?.onTrailingIconPress()}
              />
            )
          : undefined
      }
      text1={text1}
      text1NumberOfLines={2}
      text2={null}
    />
  ),
};

const styles = StyleSheet.create({
  link: {
    textDecorationLine: 'underline',
  },
  iconLarge: {
    width: 27,
    height: 27,
  },
  errorFileLeading: {
    alignSelf: 'stretch',
    width: 52,
    backgroundColor: '#DA2A52',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorFileLeadingIcon: {
    width: 26,
    height: 26,
  },
  iconFeedback: {
    width: 23,
    height: 23,
    borderRadius: 100,
  },
  iconCustomLeading: {
    width: 33,
    height: 33,
  },
  iconHidden: {
    width: 0,
    height: 0,
  },
});
